import { useChat } from '@ai-sdk/react';
import classnames from 'classnames';
import { config } from 'lib-shared-backend-config';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { useAppSelector } from 'utils/redux/useAppSelector';
import { useSidekickStarters } from 'utils/sidekickStarters';
import { useKonamiCode } from 'utils/useKonamiCode';

import { usePanelGeometry } from '../../hooks/usePanelGeometry';
import { useSidekickMessages } from '../../hooks/useSidekickMessages';
import { useSidekickPersistence } from '../../hooks/useSidekickPersistence';
import {
  createSubagentMcpClient,
  type Subagent,
  type SubagentMcpClient,
} from '../../utils/sdk/mcpClient';
import { type MimUIMessage } from '../../utils/sdk/structuredResponse';
import { createSubagentTool } from '../../utils/sdk/subagents';
import { createBrowserChatTransport } from '../../utils/sdk/transport';

import { MimSidekickComposer } from './components/mimSidekickComposer/mimSidekickComposer';
import { MimSidekickHeader } from './components/mimSidekickHeader/mimSidekickHeader';
import { MimSidekickMessageList } from './components/mimSidekickMessageList/mimSidekickMessageList';
import { MimSidekickResizeHandles } from './components/mimSidekickResizeHandles/mimSidekickResizeHandles';
import {
  MimSidekickChatProvider,
  type MimSidekickChat,
} from './mimSidekickChat.context';
import styles from './mimSidekickWidget.module.scss';

const DevPage = lazy(() => import('../devPage/devPage'));

export type MimSidekickMode = 'floating' | 'docked';

/** A prompt waiting to be sent, identified so the same text can be asked twice. */
export type PendingPrompt = {
  id: string;
  text: string;
};

type MimSidekickProps = {
  className: string;
  isMinimized: boolean;
  pendingPrompt: PendingPrompt | null;
  onPromptSent: () => void;
  onMinimize: () => void;
  onUnread: () => void;
};

type SubagentConfig = {
  mainPrompt: string;
  subagents: Subagent[];
  hasError: boolean;
  isLoaded: boolean;
};

const DEFAULT_MAIN_PROMPT = 'chat-main-agent';

const createChatId = (): string => uuid();

const EMPTY_SUBAGENT_CONFIG: SubagentConfig = {
  mainPrompt: DEFAULT_MAIN_PROMPT,
  subagents: [],
  hasError: false,
  isLoaded: false,
};

/**
 * Mim sidekick with browser-side agent loop. The main agent orchestrates the
 * loop and delegates server-side tool calls to subagents. See
 * https://ai-sdk.dev/docs/agents/subagents#specialized-orchestration for
 * details.
 */
export const MimSidekickWidget = ({
  className,
  isMinimized,
  pendingPrompt,
  onPromptSent,
  onMinimize,
  onUnread,
}: MimSidekickProps): JSX.Element => {
  const [chatId, setChatId] = useState(createChatId);

  const [subagentConfig, setSubagentConfig] = useState<SubagentConfig>(
    EMPTY_SUBAGENT_CONFIG,
  );

  const [mode, setMode] = useState<MimSidekickMode>('docked');

  useEffect(() => {
    let clients: SubagentMcpClient | null = null;
    let isCancelled = false;

    void (async () => {
      try {
        const next = await createSubagentMcpClient(config.url.rest);
        if (isCancelled) {
          await next.dispose();
          return;
        }
        clients = next;
        setSubagentConfig({
          mainPrompt: next.mainPrompt,
          subagents: next.subagents,
          hasError: next.hasError,
          isLoaded: true,
        });
      } catch {
        // Marked as loaded so a pending prompt is still sent, without tools.
        if (!isCancelled) {
          setSubagentConfig({
            ...EMPTY_SUBAGENT_CONFIG,
            hasError: true,
            isLoaded: true,
          });
        }
      }
    })();

    return () => {
      isCancelled = true;
      if (clients) void clients.dispose();
    };
  }, []);

  const conversationId = useAppSelector(
    (state) => state.conversation.metaConversation?.conversation.id,
  );

  const subagentsRef = useRef(subagentConfig.subagents);
  subagentsRef.current = subagentConfig.subagents;

  const mainPromptRef = useRef(subagentConfig.mainPrompt);
  mainPromptRef.current = subagentConfig.mainPrompt;

  const starters = useSidekickStarters();
  const { pathname } = useLocation();

  // Search params are left out to keep filter state out of the prompt.
  const contextRef = useRef('');
  contextRef.current = [
    `The user is on the page ${pathname}. Use it as context when relevant to the question.`,
    conversationId
      ? `The agent is currently viewing the Dixa conversation with id ${conversationId}. Use it as context when relevant to the question.`
      : '',
  ]
    .filter(Boolean)
    .join('\n');

  const transport = useMemo(
    () =>
      createBrowserChatTransport(
        (writer, history) =>
          Object.fromEntries(
            subagentsRef.current.map((subagent) => [
              subagent.name,
              createSubagentTool(subagent, chatId, writer, history),
            ]),
          ),
        () => mainPromptRef.current,
        () => contextRef.current,
      ),
    [chatId],
  );

  const { persistTurn, markExisting } = useSidekickPersistence();

  // onFinish always runs the newest closure, so the owner is pinned at send time.
  const streamChatIdRef = useRef(chatId);

  const { messages, sendMessage, status, setMessages } = useChat<MimUIMessage>({
    id: chatId,
    transport,
    experimental_throttle: 50,
    onFinish: ({ message, messages: turn }) => {
      if (isMinimized) onUnread();

      const prompt = turn.findLast((candidate) => candidate.role === 'user');
      // Kept after an abort or error too, so a reload still shows what arrived.
      const reply = message.parts.length > 0 ? message : undefined;

      void persistTurn({
        chatId: streamChatIdRef.current,
        messages: [prompt, reply].filter(
          (candidate) => candidate !== undefined,
        ),
      });
    },
  });

  const [isDevModeAvailable, setIsDevModeAvailable] = useState(!PRODUCTION);
  const [isDevMode, setIsDevMode] = useState(false);

  useKonamiCode(() => setIsDevModeAvailable(true));
  const { loadChat, loadOlder, isLoadingOlder } =
    useSidekickMessages(setMessages);

  const send = useCallback(
    (text: string) => {
      streamChatIdRef.current = chatId;
      void sendMessage({ text });
    },
    [chatId, sendMessage],
  );

  const { panelRef, panelStyle, startDrag, startResize, isInteracting } =
    usePanelGeometry(mode === 'floating');

  const sentPromptIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pendingPrompt || !subagentConfig.isLoaded) return;
    // 'error' is terminal, so waiting for 'ready' would hold the prompt forever.
    if (status === 'submitted' || status === 'streaming') return;
    if (sentPromptIdRef.current === pendingPrompt.id) return;
    sentPromptIdRef.current = pendingPrompt.id;
    send(pendingPrompt.text);
    onPromptSent();
  }, [pendingPrompt, subagentConfig.isLoaded, status, send, onPromptSent]);

  const chat = useMemo<MimSidekickChat>(
    () => ({
      chatId,
      startNewChat: () => setChatId(createChatId()),
      selectChat: (chatId) => {
        setChatId(chatId);
        markExisting(chatId);
        loadChat(chatId);
      },
    }),
    [chatId, loadChat, markExisting],
  );

  const isDocked = mode === 'docked';

  return (
    <MimSidekickChatProvider value={chat}>
      <aside
        ref={panelRef}
        hidden={isMinimized}
        className={classnames(
          className,
          isDocked ? styles.docked : styles.drawer,
          { [styles.interacting]: isInteracting },
        )}
        style={isDocked ? undefined : panelStyle}
      >
        <div className={styles.body}>
          <MimSidekickHeader
            onMinimize={onMinimize}
            mode={mode}
            onToggleMode={() =>
              setMode((prev) => (prev === 'floating' ? 'docked' : 'floating'))
            }
            isDevModeAvailable={isDevModeAvailable}
            isDevMode={isDevMode}
            onToggleDevMode={() => setIsDevMode((prev) => !prev)}
            dragHandleProps={
              isDocked
                ? undefined
                : {
                    onPointerDown: startDrag,
                    className: styles.dragHandle,
                  }
            }
          />
          {isDevMode ? (
            <Suspense fallback={null}>
              <DevPage />
            </Suspense>
          ) : (
            <MimSidekickMessageList
              messages={messages}
              starters={starters}
              isLoadingOlder={isLoadingOlder}
              status={status}
              onSelectChoice={send}
              onReachTop={loadOlder}
            />
          )}
          <div className={styles.composerRow}>
            <MimSidekickComposer disabled={status !== 'ready'} onSend={send} />
          </div>
        </div>
        {!isDocked && <MimSidekickResizeHandles onResizeStart={startResize} />}
      </aside>
    </MimSidekickChatProvider>
  );
};
